import {
	ReactElement,
	useContext,
	useMemo
} from "react";
import { RenderImageType } from "client/Api/AppIcon/common";
import { AppIcon } from "client/Components";
import styles from "./TwoByTwoTileContent.module.less";
import classNames from "classnames";
import { InteractionState, TileContext } from "./TileContext";
import {
	isNilOrWhiteSpace,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "./TileStatus";
import { TileMenuButton } from "./TileMenuButton";
import { useTileTooltip } from "../useTileTooltip";

export type TwoByTwoTileContentProps = {
	onClick: () => void;
	onOpenTileMenu: (coordinate: Coordinate) => void;
	customStatusIcon?: ReactElement;
};

export const TwoByTwoTileContent = (props: TwoByTwoTileContentProps): ReactElement => {
	const {
		onClick,
		onOpenTileMenu,
		customStatusIcon
	} = props;

	const {
		interactionState,
		name,
		icon,
		conditionalAccess,
		maintenanceWindow,
		appIsNotAccessible,
		appIsLicensed,
		shouldHideName: shouldHideNameFromContext
	} = useContext(TileContext);

	const isUnavailable = useMemo(
		(): boolean => {
			switch (interactionState) {
				case InteractionState.Default:
					return isNotNil(conditionalAccess)
						|| isNotNil(maintenanceWindow);
				case InteractionState.Selectable:
					return false;
				case InteractionState.Disabled:
					return true;
				default:
					never(interactionState);
			}
		},
		[
			conditionalAccess,
			interactionState,
			maintenanceWindow
		]
	);

	const shouldHideName: boolean = shouldHideNameFromContext.isSet
		? shouldHideNameFromContext.value
		: false;

	const isColorizableSvg: boolean = icon.renderType === RenderImageType.ColorizableSvg;

	const tooltip: string | undefined = useTileTooltip(
		appIsNotAccessible,
		appIsLicensed
	);

	return (
		<div
			className={classNames(
				styles.root,
				{
					[styles.root_disabled]: isUnavailable,
					[styles.root_withoutName]: shouldHideName,
					[styles.root_hoverable]: interactionState === InteractionState.Default
						|| interactionState === InteractionState.Selectable,
					[styles.root_clickable]: interactionState === InteractionState.Default
				}
			)}
			title={
				isNilOrWhiteSpace(tooltip)
					? name
					: tooltip
			}
			onClick={onClick}
		>
			<div className={styles.header}>
				<div
					className={
						classNames({
							[styles.statusIcon_shouldHideOnHover]: interactionState === InteractionState.Selectable
						})
					}
				>
					{isNotNil(customStatusIcon)
						? customStatusIcon
						: <TileStatus />
					}
				</div>
				{interactionState === InteractionState.Selectable && (
					<div className={styles.menuButton}>
						<TileMenuButton onClick={onOpenTileMenu} />
					</div>
				)}
			</div>
			<div
				className={classNames(
					styles.icon,
					{ [styles.icon_nonColorizable]: !isColorizableSvg }
				)}
			>
				<AppIcon value={icon} color="inherit" />
			</div>
			{!shouldHideName && (
				<div className={styles.footer}>
					<h4
						{...setTrackingId("name")}
						className={styles.name}
					>
						{ name }
					</h4>
				</div>
			)}
		</div>
	);
};
TwoByTwoTileContent.displayName = "TwoByTwoTileContent";

export function createTwoByTwoTileContent(props: TwoByTwoTileContentProps): ReactElement {
	return <TwoByTwoTileContent {...props} />;
}
